// @wc-ignore-file
import type { FigmaClientErrorCode, FigmaImportRow } from "./api/figma-api";
import { isFigmaSetupCode } from "./figma-messages";

/**
 * What a Figma context card shows (CR-126 stage 3):
 * - `loading`: the import row is being read;
 * - `not_imported`: no import yet — the import CTA;
 * - `importing`: an import is running (or its row is being re-read);
 * - `setup_required`: an owner/admin must connect Figma, or disconnect and connect it again, first;
 * - `error`: the import or the read failed — Retry;
 * - `ready` / `refreshing` / `stale`: an import exists (stale keeps the last
 *   good summary and document, with Retry).
 */
export type FigmaCardState =
  | { kind: "loading" }
  | { kind: "not_imported" }
  | { kind: "importing" }
  | { kind: "setup_required"; code: FigmaClientErrorCode }
  | { kind: "error"; code: FigmaClientErrorCode }
  | { kind: "ready"; row: FigmaImportRow }
  | { kind: "refreshing"; row: FigmaImportRow }
  | { kind: "stale"; row: FigmaImportRow };

export interface FigmaCardInputs {
  row: FigmaImportRow | null | undefined;
  rowLoading: boolean;
  /** A (re)read of the row is in flight — also true while it refetches. */
  rowFetching: boolean;
  rowFailed: boolean;
  importPending: boolean;
  importSucceeded: boolean;
  importErrorCode: FigmaClientErrorCode | null;
  refreshPending: boolean;
}

export function deriveFigmaCardState(input: FigmaCardInputs): FigmaCardState {
  const { row } = input;
  if (row) {
    if (input.refreshPending || row.status === "refreshing") return { kind: "refreshing", row };
    if (row.status === "stale") return { kind: "stale", row };
    return { kind: "ready", row };
  }
  if (input.importPending) return { kind: "importing" };
  if (input.importSucceeded) {
    // The row is re-read right after a successful import: keep the spinner until
    // it lands. Still nothing once the read settled means it cannot be shown.
    if (input.rowFetching) return { kind: "importing" };
    return {
      kind: "error",
      code: input.rowFailed ? "network_error" : "figma_import_failed",
    };
  }
  if (input.importErrorCode) {
    return isFigmaSetupCode(input.importErrorCode)
      ? { kind: "setup_required", code: input.importErrorCode }
      : { kind: "error", code: input.importErrorCode };
  }
  if (input.rowLoading) return { kind: "loading" };
  if (input.rowFailed) return { kind: "error", code: "network_error" };
  return { kind: "not_imported" };
}
